import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ChevronRight, Users } from "lucide-react";
import Image from "next/image";
import type { ReactNode } from "react";

interface CategoryCardProps {
	category: {
		name: string;
		description: string | null;
		image_url: string | null;
	};
	serviceCount?: number;
	isCountLoading?: boolean;
	ctaLabel?: string;
	countLabel?: string;
	showCta?: boolean;
	titleAddon?: ReactNode;
	meta?: ReactNode;
	footer?: ReactNode;
	className?: string;
}

export function CategoryCard({
	category,
	serviceCount,
	isCountLoading = false,
	ctaLabel = "Voir les activités",
	countLabel = "activités",
	showCta = true,
	titleAddon,
	meta,
	footer,
	className,
}: CategoryCardProps) {
	const description = category.description || "Découvrez les activités de cette catégorie.";
	const showCount = typeof serviceCount === "number";
	const showBottomRow = showCount || showCta;

	return (
		<Card
			className={cn(
				"group overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-0 bg-white h-full flex flex-col",
				className
			)}
		>
			<div className="relative overflow-hidden">
				<Image
					src={category.image_url || "/images/services_hero.png"}
					alt={category.name}
					width={400}
					height={300}
					className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
				/>
			</div>
			<CardContent className="p-5 flex flex-col flex-1 min-h-[10rem]">
				<div className="mb-2">
					<div className="flex items-center gap-2 mb-1.5">
						<h3 className="text-2xl font-bold text-gray-900">{category.name}</h3>
						{titleAddon}
					</div>
					<p className="text-sm text-gray-600 leading-relaxed line-clamp-2">{description}</p>
				</div>

				{meta && <div className="mb-2">{meta}</div>}

				{(showBottomRow || footer) && (
					<div className="mt-1.5 space-y-1.5">
						{showBottomRow && (
							<div className="flex items-center justify-between text-sm text-gray-500">
								{showCount ? (
									<span className="flex items-center gap-2">
										<Users className="w-4 h-4" />
										{isCountLoading ? "..." : `${serviceCount} ${countLabel}`}
									</span>
								) : (
									<span />
								)}
								{showCta && (
									<span className="flex items-center gap-2 text-emerald-600 font-semibold">
										{ctaLabel}
										<ChevronRight className="w-4 h-4" />
									</span>
								)}
							</div>
						)}
						{footer && <div>{footer}</div>}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
